import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2, Users, Stethoscope, FileText, Pill, TrendingUp,
  Mic, FileCheck, Sparkles, AlertTriangle, BarChart3, Activity,
  CheckCircle, XCircle, ArrowUpRight, Brain, History, BrainCircuit
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIE_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function StatCard({ label, value, icon: Icon, gradient, shadow, delay = 0 }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm card-interactive" style={{ animationDelay: `${delay}ms` }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">{label}</p>
            <p className="text-3xl font-heading font-semibold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-lg ${shadow}`}>
            <Icon className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RateBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-800">{value}/{max} ({pct}%)</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AnalyticsDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [successPatterns, setSuccessPatterns] = useState(null);
  const [learningInsights, setLearningInsights] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, trRes, orRes, spRes, liRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/overview`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/treatment-trends`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/ai-overrides`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/success-patterns`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/learning-insights`, { withCredentials: true }),
      ]);
      setOverview(ovRes.data);
      setTrends(trRes.data);
      setOverrides(orRes.data);
      setSuccessPatterns(spRes.data);
      setLearningInsights(liRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!overview) return null;

  const outcomeData = trends ? Object.entries(trends.follow_up_outcomes).map(([key, val]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1), value: val
  })) : [];

  const adherenceData = trends ? Object.entries(trends.adherence_distribution).map(([key, val]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1), value: val
  })) : [];

  const topMedsData = trends?.top_medicines?.slice(0, 10) || [];

  const modReasonData = overrides ? Object.entries(overrides.modification_reasons).map(([key, val]) => ({
    name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), count: val
  })) : [];

  const consultTypeData = trends ? Object.entries(trends.consultation_types).map(([key, val]) => ({
    name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: val
  })) : [];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="analytics-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Analytics</h1>
          <p className="text-slate-500 mt-1">Workflow intelligence and clinical insights</p>
        </div>
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5 border-blue-200 bg-blue-50 text-blue-700">
          <Activity className="w-3.5 h-3.5" />
          Live Data
        </Badge>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Patients" value={overview.counts.patients} icon={Users} gradient="from-blue-500 to-blue-600" shadow="shadow-blue-500/20" delay={0} />
        <StatCard label="Consultations" value={overview.counts.consultations} icon={Stethoscope} gradient="from-emerald-500 to-emerald-600" shadow="shadow-emerald-500/20" delay={80} />
        <StatCard label="Prescriptions" value={overview.counts.prescriptions} icon={FileText} gradient="from-violet-500 to-violet-600" shadow="shadow-violet-500/20" delay={160} />
        <StatCard label="Follow-Ups" value={overview.counts.follow_ups} icon={TrendingUp} gradient="from-amber-500 to-amber-600" shadow="shadow-amber-500/20" delay={240} />
        <StatCard label="Medicines" value={overview.counts.medicines} icon={Pill} gradient="from-rose-500 to-rose-600" shadow="shadow-rose-500/20" delay={320} />
      </div>

      <Tabs defaultValue="pipeline" className="space-y-5">
        <TabsList className="grid w-full grid-cols-5 bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="pipeline" className="rounded-lg flex items-center gap-1.5 text-xs"><Brain className="w-3.5 h-3.5" />AI Pipeline</TabsTrigger>
          <TabsTrigger value="treatments" className="rounded-lg flex items-center gap-1.5 text-xs"><Pill className="w-3.5 h-3.5" />Treatments</TabsTrigger>
          <TabsTrigger value="overrides" className="rounded-lg flex items-center gap-1.5 text-xs"><History className="w-3.5 h-3.5" />Overrides</TabsTrigger>
          <TabsTrigger value="outcomes" className="rounded-lg flex items-center gap-1.5 text-xs"><TrendingUp className="w-3.5 h-3.5" />Outcomes</TabsTrigger>
          <TabsTrigger value="learning" className="rounded-lg flex items-center gap-1.5 text-xs" data-testid="learning-tab"><BrainCircuit className="w-3.5 h-3.5" />Learning</TabsTrigger>
        </TabsList>

        {/* AI Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Pipeline Stats */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><Mic className="w-5 h-5 text-blue-600" />Recording & Transcription</CardTitle>
                <CardDescription>Audio processing pipeline metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-xl bg-blue-50">
                    <p className="text-2xl font-heading font-semibold text-blue-700" data-testid="stat-recordings">{overview.ai_pipeline.recordings}</p>
                    <p className="text-[10px] uppercase tracking-wider text-blue-500 font-bold">Recordings</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-emerald-50">
                    <p className="text-2xl font-heading font-semibold text-emerald-700" data-testid="stat-transcripts">{overview.ai_pipeline.transcripts}</p>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-bold">Transcripts</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-violet-50">
                    <p className="text-2xl font-heading font-semibold text-violet-700" data-testid="stat-summaries">{overview.ai_pipeline.summaries}</p>
                    <p className="text-[10px] uppercase tracking-wider text-violet-500 font-bold">Summaries</p>
                  </div>
                </div>
                <RateBar label="Transcript Success" value={overview.ai_pipeline.transcripts} max={overview.ai_pipeline.recordings} color="bg-emerald-500" />
                <RateBar label="Summary Success" value={overview.ai_pipeline.summaries} max={overview.ai_pipeline.transcripts || 1} color="bg-violet-500" />
                {overview.ai_pipeline.failed > 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 border border-rose-100">
                    <AlertTriangle className="w-4 h-4 text-rose-500" />
                    <span className="text-sm text-rose-700 font-medium">{overview.ai_pipeline.failed} failed processing</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Prescription Stats */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" />AI Prescriptions</CardTitle>
                <CardDescription>AI draft generation and doctor overrides</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-xl bg-violet-50">
                    <p className="text-2xl font-heading font-semibold text-violet-700" data-testid="stat-drafts">{overview.ai_prescriptions.drafts_generated}</p>
                    <p className="text-[10px] uppercase tracking-wider text-violet-500 font-bold">Drafts</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-amber-50">
                    <p className="text-2xl font-heading font-semibold text-amber-700" data-testid="stat-overrides">{overview.ai_prescriptions.overrides}</p>
                    <p className="text-[10px] uppercase tracking-wider text-amber-500 font-bold">Overrides</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-blue-50">
                    <p className="text-2xl font-heading font-semibold text-blue-700">{overview.ai_prescriptions.override_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-blue-500 font-bold">Override Rate</p>
                  </div>
                </div>
                {overrides && overrides.total_overrides > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Override Diff Summary</p>
                    <div className="flex gap-3">
                      <div className="flex items-center gap-1.5 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-slate-600">+{overrides.diff_totals.medicines_added} added</span></div>
                      <div className="flex items-center gap-1.5 text-sm"><XCircle className="w-4 h-4 text-rose-500" /><span className="text-slate-600">-{overrides.diff_totals.medicines_removed} removed</span></div>
                      <div className="flex items-center gap-1.5 text-sm"><ArrowUpRight className="w-4 h-4 text-amber-500" /><span className="text-slate-600">{overrides.diff_totals.medicines_changed} changed</span></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Consultation Types */}
            {consultTypeData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2"><Stethoscope className="w-5 h-5 text-emerald-600" />Consultation Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={consultTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                        {consultTypeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Treatments Tab */}
        <TabsContent value="treatments" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top Medicines */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" />Most Used Medicines</CardTitle>
                <CardDescription>Top medicines across all prescriptions</CardDescription>
              </CardHeader>
              <CardContent>
                {topMedsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topMedsData} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#475569' }} width={160} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                      <Bar dataKey="count" fill="#2563EB" radius={[0, 6, 6, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No prescription data yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Overrides Tab */}
        <TabsContent value="overrides" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Modification Reasons Chart */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><History className="w-5 h-5 text-amber-600" />Modification Reasons</CardTitle>
                <CardDescription>Why prescriptions are modified</CardDescription>
              </CardHeader>
              <CardContent>
                {modReasonData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={modReasonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} angle={-30} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                      <Bar dataKey="count" fill="#F59E0B" radius={[6, 6, 0, 0]} barSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No modification data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Overrides */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><Brain className="w-5 h-5 text-violet-600" />Recent AI Overrides</CardTitle>
                <CardDescription>Latest doctor modifications to AI drafts</CardDescription>
              </CardHeader>
              <CardContent>
                {overrides?.recent_overrides?.length > 0 ? (
                  <div className="space-y-3">
                    {overrides.recent_overrides.map((o, i) => (
                      <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className="text-xs capitalize">{o.override_reason.replace(/_/g, ' ') || 'General'}</Badge>
                          <span className="text-xs text-slate-400">{o.doctor_name}</span>
                        </div>
                        {o.override_text && <p className="text-xs text-slate-600 mt-1">{o.override_text}</p>}
                        <div className="flex gap-2 mt-2 text-xs">
                          <span className="text-emerald-600">+{o.diff_summary.added}</span>
                          <span className="text-rose-600">-{o.diff_summary.removed}</span>
                          <span className="text-amber-600">{o.diff_summary.changed} changed</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No AI overrides recorded yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Outcomes Tab */}
        <TabsContent value="outcomes" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Follow-up Outcomes */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" />Treatment Outcomes</CardTitle>
                <CardDescription>Follow-up response distribution</CardDescription>
              </CardHeader>
              <CardContent>
                {outcomeData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={outcomeData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name" label>
                        {outcomeData.map((_, i) => <Cell key={i} fill={['#10B981', '#F59E0B', '#94A3B8', '#EF4444'][i]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No follow-up data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Adherence */}
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><FileCheck className="w-5 h-5 text-blue-600" />Patient Adherence</CardTitle>
                <CardDescription>How well patients follow prescriptions</CardDescription>
              </CardHeader>
              <CardContent>
                {adherenceData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={adherenceData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name" label>
                        {adherenceData.map((_, i) => <Cell key={i} fill={['#10B981', '#F59E0B', '#EF4444'][i]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No adherence data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Success Patterns */}
            {successPatterns && (
              <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-600" />Success Patterns</CardTitle>
                  <CardDescription>Medicines associated with improved vs worse outcomes ({successPatterns.total_follow_ups_analyzed} follow-ups analyzed)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-emerald-500 mb-3">Improved Outcomes</p>
                      {successPatterns.medicines_in_improved_outcomes.length > 0 ? (
                        <div className="space-y-2">
                          {successPatterns.medicines_in_improved_outcomes.map(([name, count], i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-emerald-50">
                              <span className="text-sm font-medium text-emerald-800">{name}</span>
                              <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">{count}x</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No data yet</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-rose-500 mb-3">Worse Outcomes</p>
                      {successPatterns.medicines_in_worse_outcomes.length > 0 ? (
                        <div className="space-y-2">
                          {successPatterns.medicines_in_worse_outcomes.map(([name, count], i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-rose-50">
                              <span className="text-sm font-medium text-rose-800">{name}</span>
                              <Badge variant="outline" className="text-xs bg-rose-100 text-rose-700 border-rose-200">{count}x</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No data yet</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Learning Tab */}
        <TabsContent value="learning" className="space-y-5" data-testid="learning-tab-content">
          <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <BrainCircuit className="w-5 h-5 text-violet-600" />
                    AI Learning Feedback Loop
                  </CardTitle>
                  <CardDescription>The AI learns from doctor overrides to generate smarter prescription drafts over time</CardDescription>
                </div>
                <Badge variant="outline" className={`gap-1.5 px-3 py-1.5 ${learningInsights?.is_active ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}>
                  <BrainCircuit className="w-3.5 h-3.5" />
                  {learningInsights?.is_active ? 'Active' : 'No Data Yet'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {!learningInsights?.is_active ? (
                <div className="text-center py-8">
                  <BrainCircuit className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">The AI learning system activates once doctors start overriding AI prescription drafts.</p>
                  <p className="text-xs text-muted-foreground mt-1">Patterns are detected after 2+ similar overrides by a doctor.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {learningInsights.doctors?.map((doc) => (
                    <div key={doc.doctor_id} className="p-4 rounded-xl border border-violet-100 bg-violet-50/30" data-testid={`learning-doctor-${doc.doctor_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading font-medium text-slate-800">{doc.doctor_name}</h3>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs bg-violet-100 text-violet-700 border-violet-200">{doc.total_overrides} overrides</Badge>
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">{doc.substitutions} substitutions</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Substitutions */}
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-2">Substitution Patterns</p>
                          {doc.patterns.medicine_substitutions.length > 0 ? (
                            <div className="space-y-1.5">
                              {doc.patterns.medicine_substitutions.map((sub, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs p-2 rounded-lg bg-white border border-slate-100">
                                  <span className="text-slate-600">{sub.from_to}</span>
                                  <Badge className="ml-auto text-[10px] bg-amber-100 text-amber-700 border-0">{sub.count}x</Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No recurring substitutions detected</p>
                          )}
                        </div>
                        {/* Additions */}
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-2">Frequently Added</p>
                          {doc.patterns.common_additions.length > 0 ? (
                            <div className="space-y-1.5">
                              {doc.patterns.common_additions.map((add, i) => (
                                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-white border border-slate-100">
                                  <span className="text-slate-600">{add.medicine}</span>
                                  <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-0">{add.count}x</Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No recurring additions detected</p>
                          )}
                        </div>
                        {/* Removals */}
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600 mb-2">Frequently Removed</p>
                          {doc.patterns.common_removals.length > 0 ? (
                            <div className="space-y-1.5">
                              {doc.patterns.common_removals.map((rem, i) => (
                                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-white border border-slate-100">
                                  <span className="text-slate-600">{rem.medicine}</span>
                                  <Badge className="text-[10px] bg-rose-100 text-rose-700 border-0">{rem.count}x</Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No recurring removals detected</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
